import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Space,
  Button,
  DatePicker,
  Select,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  message,
  Descriptions,
  Tag,
  Empty,
  Spin
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  UserOutlined,
  FileTextOutlined,
  PrinterOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import invoiceService, {
  CustomerStatementData,
  CustomerStatementTransaction
} from '@/services/invoiceService';
import customerService from '@/services/customerService';
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

interface Customer {
  id: string;
  name: string;
  email?: string;
}

const CustomerStatement: React.FC = () => {
    const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [statementData, setStatementData] = useState<CustomerStatementData | null>(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      fetchStatement();
    }
  }, [selectedCustomerId, dateRange]);

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const data = await customerService.getAllCustomers();
      setCustomers(data);
    } catch (error: any) {
      message.error(error.message || t('Failed to fetch customers'));
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchStatement = async () => {
    if (!selectedCustomerId) return;

    setLoading(true);
    try {
      const data = await invoiceService.getCustomerStatement(
        selectedCustomerId,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setStatementData(data);
    } catch (error: any) {
      message.error(error.message || t('Failed to fetch customer statement'));
      setStatementData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerChange = (customerId: string) => {
    setSelectedCustomerId(customerId);
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const handleRefresh = () => {
    fetchStatement();
  };

  const handleExport = () => {
    message.info(t('Export feature coming soon'));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const generatePrintContent = (): string => {
    if (!statementData) return '';

    const logoUrl = `${window.location.origin}/shoptoplus.png`;
    const companyName = 'Shoptoplus Limited';

    const transactionRows = statementData.transactions.map((txn: CustomerStatementTransaction) => `
      <tr>
        <td>${dayjs(txn.date).format('YYYY-MM-DD')}</td>
        <td><span class="tag ${txn.type === 'INVOICE' ? 'tag-invoice' : 'tag-payment'}">${txn.type}</span></td>
        <td>${txn.reference}</td>
        <td>${txn.description || '-'}</td>
        <td class="text-right ${txn.debit > 0 ? 'amount-debit' : ''}">${txn.debit > 0 ? formatCurrency(txn.debit) : '-'}</td>
        <td class="text-right ${txn.credit > 0 ? 'amount-credit' : ''}">${txn.credit > 0 ? formatCurrency(txn.credit) : '-'}</td>
        <td class="text-right ${txn.balance > 0 ? 'amount-debit' : txn.balance < 0 ? 'amount-credit' : ''}">
          <strong>${formatCurrency(Math.abs(txn.balance))}${txn.balance !== 0 ? (txn.balance > 0 ? ' DR' : ' CR') : ''}</strong>
        </td>
      </tr>
    `).join('');

    return `
      <table class="print-wrapper">
        <tfoot>
          <tr>
            <td>
              <div class="page-footer-content">
                <p>This is a computer-generated statement and does not require a signature.</p>
                <p>For any queries, please contact our accounts department.</p>
                <p>Generated on ${dayjs().format('MMMM D, YYYY [at] h:mm A')}</p>
              </div>
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td>
              <div class="statement-header">
                <div class="company-info">
                  <img src="${logoUrl}" alt="Logo" class="company-logo" />
                  <div class="company-details">
                    <div class="company-name">${companyName}</div>
                    <div class="company-address">
                      5/F, Block B, Profit Industrial Building<br>
                      1-15 Kwai Fung Crescent<br>
                      Kwai Chung<br>
                      Hong Kong
                    </div>
                  </div>
                </div>
                <div class="statement-title-section">
                  <div class="statement-title">CUSTOMER STATEMENT</div>
                  <div class="statement-period">
                    ${dayjs(statementData.period.fromDate).format('DD/MM/YYYY')} - ${dayjs(statementData.period.toDate).format('DD/MM/YYYY')}
                  </div>
                </div>
              </div>

              <div class="info-section">
                <div class="customer-info">
                  <div class="section-title">Bill To:</div>
                  <div class="info-row"><span class="info-label">Name:</span><span class="info-value"><strong>${statementData.customer.name}</strong></span></div>
                  <div class="info-row"><span class="info-label">Code:</span><span class="info-value">${statementData.customer.code}</span></div>
                  ${statementData.customer.email ? `<div class="info-row"><span class="info-label">Email:</span><span class="info-value">${statementData.customer.email}</span></div>` : ''}
                  ${statementData.customer.phone ? `<div class="info-row"><span class="info-label">Phone:</span><span class="info-value">${statementData.customer.phone}</span></div>` : ''}
                  ${statementData.customer.address ? `<div class="info-row"><span class="info-label">Address:</span><span class="info-value">${statementData.customer.address}</span></div>` : ''}
                </div>
                <div class="statement-info">
                  <div class="section-title">Statement Details:</div>
                  <div class="info-row"><span class="info-label">Statement Date:</span><span class="info-value">${dayjs().format('MMMM D, YYYY')}</span></div>
                  <div class="info-row"><span class="info-label">Account Status:</span><span class="info-value">${statementData.closingBalance > 0 ? 'Amount Due' : statementData.closingBalance < 0 ? 'Credit Balance' : 'Settled'}</span></div>
                </div>
              </div>

              <div class="summary-section">
                <div class="summary-item">
                  <div class="summary-label">Opening Balance</div>
                  <div class="summary-value ${statementData.openingBalance > 0 ? 'debit' : statementData.openingBalance < 0 ? 'credit' : ''}">
                    ${formatCurrency(Math.abs(statementData.openingBalance))}${statementData.openingBalance !== 0 ? (statementData.openingBalance > 0 ? ' DR' : ' CR') : ''}
                  </div>
                </div>
                <div class="summary-item">
                  <div class="summary-label">Total Invoices</div>
                  <div class="summary-value debit">${formatCurrency(statementData.summary.totalInvoices)}</div>
                </div>
                <div class="summary-item">
                  <div class="summary-label">Total Payments</div>
                  <div class="summary-value credit">${formatCurrency(statementData.summary.totalPayments)}</div>
                </div>
                <div class="summary-item">
                  <div class="summary-label">Closing Balance</div>
                  <div class="summary-value ${statementData.closingBalance > 0 ? 'debit' : statementData.closingBalance < 0 ? 'credit' : ''}">
                    ${formatCurrency(Math.abs(statementData.closingBalance))}${statementData.closingBalance !== 0 ? (statementData.closingBalance > 0 ? ' DR' : ' CR') : ''}
                  </div>
                </div>
              </div>

              <table>
                <thead>
                  <tr>
                    <th style="width: 100px">Date</th>
                    <th style="width: 80px">Type</th>
                    <th style="width: 120px">Reference</th>
                    <th>Description</th>
                    <th style="width: 100px" class="text-right">Debit</th>
                    <th style="width: 100px" class="text-right">Credit</th>
                    <th style="width: 110px" class="text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="balance-row">
                    <td>${dayjs(statementData.period.fromDate).format('YYYY-MM-DD')}</td>
                    <td>-</td>
                    <td>-</td>
                    <td><strong>Opening Balance</strong></td>
                    <td class="text-right">-</td>
                    <td class="text-right">-</td>
                    <td class="text-right">
                      <strong>${formatCurrency(Math.abs(statementData.openingBalance))}${statementData.openingBalance !== 0 ? (statementData.openingBalance > 0 ? ' DR' : ' CR') : ''}</strong>
                    </td>
                  </tr>
                  ${transactionRows}
                  <tr class="total-row">
                    <td colspan="4"><strong>Closing Balance</strong></td>
                    <td class="text-right amount-debit"><strong>${formatCurrency(statementData.summary.totalInvoices)}</strong></td>
                    <td class="text-right amount-credit"><strong>${formatCurrency(statementData.summary.totalPayments)}</strong></td>
                    <td class="text-right ${statementData.closingBalance > 0 ? 'amount-debit' : statementData.closingBalance < 0 ? 'amount-credit' : ''}">
                      <strong>${formatCurrency(Math.abs(statementData.closingBalance))}${statementData.closingBalance !== 0 ? (statementData.closingBalance > 0 ? ' DR' : ' CR') : ''}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>

              ${statementData.closingBalance > 0 ? `
                <div style="border: 2px solid #cf1322; padding: 15px; text-align: center; margin-bottom: 20px; background: #fff2f0;">
                  <div style="font-size: 14px; color: #cf1322; margin-bottom: 5px;"><strong>AMOUNT DUE</strong></div>
                  <div style="font-size: 24px; font-weight: bold; color: #cf1322;">${formatCurrency(statementData.closingBalance)}</div>
                </div>
              ` : ''}
            </td>
          </tr>
        </tbody>
      </table>
    `;
  };

  const handlePrint = () => {
    if (!statementData) return;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const printContent = generatePrintContent();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Customer Statement - ${statementData.customer.name}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; padding: 20px; }
            .statement-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; margin-bottom: 30px; border-bottom: 3px solid #00bcd4; }
            .company-info { flex: 1; display: flex; align-items: flex-start; gap: 18px; }
            .company-logo { width: 110px; height: auto; flex-shrink: 0; }
            .company-details { flex: 1; }
            .company-name { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 4px; }
            .company-address { font-size: 10px; line-height: 1.4; color: #666; }
            .statement-title-section { text-align: right; }
            .statement-title { font-size: 26px; font-weight: bold; color: #00bcd4; }
            .statement-period { font-size: 14px; font-weight: bold; color: #666; margin-top: 4px; }
            .info-section { display: flex; justify-content: space-between; margin-bottom: 25px; }
            .customer-info, .statement-info { width: 48%; }
            .section-title { font-weight: bold; font-size: 14px; margin-bottom: 10px; color: #00bcd4; }
            .info-row { display: flex; margin-bottom: 4px; }
            .info-label { width: 100px; color: #666; }
            .info-value { flex: 1; }
            .summary-section { display: flex; justify-content: space-around; background: #f5f5f5; padding: 15px; margin-bottom: 25px; border-radius: 4px; }
            .summary-item { text-align: center; }
            .summary-label { font-size: 11px; color: #666; margin-bottom: 5px; }
            .summary-value { font-size: 16px; font-weight: bold; }
            .summary-value.debit { color: #cf1322; }
            .summary-value.credit { color: #3f8600; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background: #00bcd4; color: white; font-weight: bold; font-size: 11px; text-transform: uppercase; }
            td { font-size: 11px; }
            .text-right { text-align: right; }
            .amount-debit { color: #cf1322; }
            .amount-credit { color: #3f8600; }
            .balance-row { background: #f9f9f9; font-weight: bold; }
            .total-row { background: #e6f7ff; font-weight: bold; }
            .tag { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: bold; }
            .tag-invoice { background: #e6f7ff; color: #1890ff; }
            .tag-payment { background: #f6ffed; color: #52c41a; }
            table.print-wrapper { width: 100%; border: none; border-collapse: collapse; }
            table.print-wrapper > thead, table.print-wrapper > tbody, table.print-wrapper > tfoot { border: none; }
            table.print-wrapper > tbody > tr > td, table.print-wrapper > tfoot > tr > td { border: none; padding: 0; vertical-align: top; }
            .page-footer-content { padding-top: 15px; margin-top: 20px; border-top: 1px solid #ddd; font-size: 9px; color: #666; text-align: center; }
            .page-footer-content p { margin: 2px 0; }
            @media print {
              body { padding: 10px; }
              @page { margin: 10mm; }
              tr { page-break-inside: avoid; }
              thead { display: table-header-group; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  const columns: ColumnsType<CustomerStatementTransaction> = [
    {
      title: t('Date'),
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: t('Type'),
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const color = type === 'INVOICE' ? 'blue' : type === 'CREDIT_NOTE' ? 'orange' : 'green';
        const label = type === 'CREDIT_NOTE' ? 'CREDIT NOTE' : type;
        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: t('Reference'),
      dataIndex: 'reference',
      key: 'reference',
      width: 150,
      render: (ref) => <Text code>{ref}</Text>
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      width: 250
    },
    {
      title: t('Debit'),
      dataIndex: 'debit',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#cf1322' : undefined }}>
          {value > 0 ? formatCurrency(value) : '-'}
        </Text>
      )
    },
    {
      title: t('Credit'),
      dataIndex: 'credit',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#3f8600' : undefined }}>
          {value > 0 ? formatCurrency(value) : '-'}
        </Text>
      )
    },
    {
      title: t('Balance'),
      dataIndex: 'balance',
      key: 'balance',
      width: 130,
      align: 'right',
      render: (value) => (
        <Text strong style={{ color: value > 0 ? '#cf1322' : value < 0 ? '#3f8600' : undefined }}>
          {formatCurrency(Math.abs(value))}
          {value !== 0 && (value > 0 ? ' DR' : ' CR')}
        </Text>
      )
    }
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              {t('Customer Statement')}
                                      </Title>
            <Text type="secondary">
              {t('View transaction history and balance for a customer')}
                                      </Text>
          </Col>
        </Row>

        <Divider />

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Select
              showSearch
              style={{ width: '100%' }}
              placeholder={t('Select a customer')}
              loading={customersLoading}
              onChange={handleCustomerChange}
              value={selectedCustomerId}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={customers.map(c => ({
                value: c.id,
                label: c.name
              }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                disabled={!selectedCustomerId}
              >
                {t('Refresh')}
                                            </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={handlePrint}
                disabled={!statementData}
              >
                {t('Print')}
                                            </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={!statementData}
              >
                {t('Export')}
                                            </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {!selectedCustomerId ? (
        <Card style={{ marginTop: 16 }}>
          <Empty
            image={<UserOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description={t('Select a customer to view their statement')}
          />
        </Card>
      ) : loading ? (
        <Card style={{ marginTop: 16 }}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text type="secondary">{t('Loading statement...')}</Text>
            </div>
          </div>
        </Card>
      ) : statementData ? (
        <>
          <Card style={{ marginTop: 16 }}>
            <Descriptions title={t('Customer Information')} bordered column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label={t('Name')}>
                <Text strong>{statementData.customer.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('Code')}>
                <Text code>{statementData.customer.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('Email')}>
                {statementData.customer.email || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Phone')}>
                {statementData.customer.phone || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('Address')} span={2}>
                {statementData.customer.address || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title={t('Opening Balance')}
                  value={Math.abs(statementData.openingBalance)}
                  precision={2}
                  prefix="$"
                  suffix={statementData.openingBalance !== 0 ? (statementData.openingBalance > 0 ? 'DR' : 'CR') : ''}
                  valueStyle={{
                    color: statementData.openingBalance > 0 ? '#cf1322' : statementData.openingBalance < 0 ? '#3f8600' : undefined
                  }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title={t('Total Invoices')}
                  value={statementData.summary.totalInvoices}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title={t('Total Payments')}
                  value={statementData.summary.totalPayments}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title={t('Total Credit Notes')}
                  value={statementData.summary.totalCredits || 0}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title={t('Closing Balance')}
                  value={Math.abs(statementData.closingBalance)}
                  precision={2}
                  prefix="$"
                  suffix={statementData.closingBalance !== 0 ? (statementData.closingBalance > 0 ? 'DR' : 'CR') : ''}
                  valueStyle={{
                    color: statementData.closingBalance > 0 ? '#cf1322' : statementData.closingBalance < 0 ? '#3f8600' : undefined,
                    fontWeight: 'bold'
                  }}
                />
              </Col>
            </Row>
          </Card>

          <Card style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                {t('Statement Period:')} {dayjs(statementData.period.fromDate).format('YYYY-MM-DD')} to {dayjs(statementData.period.toDate).format('YYYY-MM-DD')}
              </Text>
            </div>

            <Table
              dataSource={statementData.transactions}
              columns={columns}
              rowKey={(record, index) => `${record.reference}-${index}`}
              pagination={false}
              size="small"
              bordered
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <Text strong>{t('Opening Balance')}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} colSpan={2} />
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong>
                        {formatCurrency(Math.abs(statementData.openingBalance))}
                        {statementData.openingBalance !== 0 && (statementData.openingBalance > 0 ? ' DR' : ' CR')}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                  <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <Text strong>{t('Closing Balance')}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong style={{ color: '#cf1322' }}>
                        {formatCurrency(statementData.summary.totalInvoices)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Text strong style={{ color: '#3f8600' }}>
                        {formatCurrency(statementData.summary.totalPayments)}
                      </Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong style={{
                        color: statementData.closingBalance > 0 ? '#cf1322' : statementData.closingBalance < 0 ? '#3f8600' : undefined
                      }}>
                        {formatCurrency(Math.abs(statementData.closingBalance))}
                        {statementData.closingBalance !== 0 && (statementData.closingBalance > 0 ? ' DR' : ' CR')}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {statementData.transactions.length === 0 && (
              <Empty
                description={t('No transactions found for this period')}
                style={{ marginTop: 32 }}
              />
            )}
          </Card>
        </>
      ) : (
        <Card style={{ marginTop: 16 }}>
          <Empty description={t('Failed to load statement data')} />
        </Card>
      )}
    </div>
  );
};

export default CustomerStatement;